import { useCallback, useEffect, useState } from "react";
import { Button, Card, Space, Switch, Table, Tag, Modal, Form, Input, Message } from "@arco-design/web-react";
import { ScheduledJobAPI } from "@/apis/system";
import OperationButton from "@/components/OperationButton";
import { DateTime } from "@/components/DateTime";
import { IconRefresh, IconThunderbolt, IconEdit } from "@arco-design/web-react/icon";

const FormItem = Form.Item;

const STATUS_TAG_MAP = {
    SUCCESS: { color: 'green', text: 'Success' },
    FAILED: { color: 'red', text: 'Failed' },
    RUNNING: { color: 'blue', text: 'Running' },
};

const formatDuration = (ms) => {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
};

const humanizeCron = (cron) => {
    if (!cron) return '';
    const parts = cron.trim().split(/\s+/);
    if (parts.length < 6) return cron;
    const [sec, min, hour, day, month, dow] = parts;
    if (day === '*' && month === '*' && dow === '?') {
        if (min === '0' && sec === '0') return `Every hour at :00`;
        if (hour.includes(',')) return `Twice daily at ${hour.split(',').map(h => `${h.trim()}:${min.padStart(2, '0')}`).join(' & ')}`;
        return `Daily at ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
    }
    return cron;
};

export const JobManagement = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editVisible, setEditVisible] = useState(false);
    const [editJob, setEditJob] = useState(null);
    const [editForm] = Form.useForm();
    const [triggerLoading, setTriggerLoading] = useState({});

    const refreshJobs = useCallback(async () => {
        setLoading(true);
        try {
            const data = await ScheduledJobAPI.getAllJobs();
            setJobs(data);
        } catch (e) {
            Message.error('Failed to load jobs');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshJobs();
    }, [refreshJobs]);

    const handleToggle = async (id, enabled) => {
        try {
            await ScheduledJobAPI.toggleJob(id, enabled);
            Message.success(enabled ? 'Job enabled' : 'Job disabled');
            refreshJobs();
        } catch (e) {
            Message.error('Failed to toggle job');
        }
    };

    const handleTrigger = async (jobKey) => {
        setTriggerLoading(prev => ({ ...prev, [jobKey]: true }));
        try {
            await ScheduledJobAPI.triggerJob(jobKey);
            Message.success(`Job "${jobKey}" triggered`);
            setTimeout(() => refreshJobs(), 2000);
        } catch (e) {
            Message.error('Failed to trigger job');
        } finally {
            setTriggerLoading(prev => ({ ...prev, [jobKey]: false }));
        }
    };

    const handleEdit = (record) => {
        setEditJob(record);
        editForm.setFieldsValue({
            id: record.id,
            jobKey: record.jobKey,
            jobName: record.jobName,
            cronExpression: record.cronExpression,
            description: record.description,
        });
        setEditVisible(true);
    };

    const handleEditSubmit = async () => {
        try {
            const values = await editForm.validate();
            await ScheduledJobAPI.updateJob(values);
            Message.success('Job updated');
            setEditVisible(false);
            refreshJobs();
        } catch (e) {
            if (e !== 'form validation failed') {
                Message.error('Failed to update job');
            }
        }
    };

    const columns = [
        {
            title: 'Job Name',
            dataIndex: 'jobName',
            width: 200,
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{text}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-3)' }}>{record.jobKey}</div>
                </div>
            ),
        },
        {
            title: 'Schedule',
            dataIndex: 'cronExpression',
            width: 220,
            render: (cron) => (
                <div>
                    <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{cron}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-3)' }}>{humanizeCron(cron)}</div>
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'enabled',
            width: 100,
            render: (enabled) => enabled
                ? <Tag color='green'>Enabled</Tag>
                : <Tag color='gray'>Disabled</Tag>,
        },
        {
            title: 'Last Execution',
            width: 200,
            render: (_, record) => {
                const statusInfo = STATUS_TAG_MAP[record.lastStatus] || {};
                return (
                    <div>
                        {record.lastStartTime ? (
                            <>
                                <div style={{ fontSize: 12 }}><DateTime value={record.lastStartTime} /></div>
                                <Space size={4}>
                                    {record.lastStatus && <Tag color={statusInfo.color} size='small'>{statusInfo.text}</Tag>}
                                    {record.lastDurationMs != null && (
                                        <span style={{ fontSize: 12, color: 'var(--color-text-3)' }}>{formatDuration(record.lastDurationMs)}</span>
                                    )}
                                </Space>
                            </>
                        ) : (
                            <span style={{ color: 'var(--color-text-3)' }}>Never</span>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Next Fire',
            dataIndex: 'nextFireTime',
            width: 160,
            render: (val) => val ? <DateTime value={val} /> : '-',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            ellipsis: true,
        },
        {
            title: 'Actions',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Switch
                        size='small'
                        checked={record.enabled}
                        onChange={(val) => handleToggle(record.id, val)}
                    />
                    <OperationButton
                        icon={<IconThunderbolt />}
                        label='Trigger Now'
                        loading={triggerLoading[record.jobKey]}
                        onClick={() => handleTrigger(record.jobKey)}
                    />
                    <OperationButton
                        icon={<IconEdit />}
                        label='Edit'
                        onClick={() => handleEdit(record)}
                    />
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="Scheduled Jobs"
            extra={
                <Button icon={<IconRefresh />} onClick={refreshJobs} loading={loading}>
                    Refresh
                </Button>
            }
        >
            <Table
                rowKey="id"
                columns={columns}
                data={jobs}
                loading={loading}
                pagination={false}
                scroll={{ x: 1200 }}
                size='small'
            />

            <Modal
                title="Edit Job"
                visible={editVisible}
                onOk={handleEditSubmit}
                onCancel={() => setEditVisible(false)}
                style={{ width: 500 }}
            >
                <Form form={editForm} layout="vertical">
                    <FormItem field="id" hidden><Input /></FormItem>
                    <FormItem field="jobKey" hidden><Input /></FormItem>
                    <FormItem label="Job Name" field="jobName">
                        <Input disabled />
                    </FormItem>
                    <FormItem
                        label="Cron Expression"
                        field="cronExpression"
                        rules={[{ required: true, message: 'Cron expression is required' }]}
                        extra={editJob ? humanizeCron(editJob.cronExpression) : ''}
                    >
                        <Input placeholder="e.g. 0 0 1 * * ?" />
                    </FormItem>
                    <FormItem label="Description" field="description">
                        <Input.TextArea rows={3} />
                    </FormItem>
                </Form>
            </Modal>
        </Card>
    );
};
